import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { path, pick, prop } from 'ramda';

import { usePortletConfig } from 'js/components/system/Portlet';

import {
    useGameFilter
} from '@interlayer/js/app/components/main/Games/components/GameCategory/components/GameList/hooks/useGameFilter';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';

import { useGamesSelector } from 'js/app/components/main/Games/store/context';
import { useGetTemporaryAuthenticationToken } from 'js/hooks/auth';
import { setFutureAnthemsGames } from 'js/hooks/user/store/buzz/actions';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { post } from 'js/utils/fetch.util';

const UPDATE_TIME = 1;

const parseResponse = ({
    response, recommendationTypeConfig
}) => {
    return path(
        recommendationTypeConfig?.responsePath?.split('.'),
        response.data
    ) || [];
};

const useFutureAnthemsRequestParams = () => {
    const app = useAppSelector();
    const user = useUserSelector();
    const device = useDeviceSelector();
    const dataMap = useMemo(() => ({
        app,
        user,
        device
    }), [app, device, user]);

    return useCallback((recommendationTypeConfig) => {
        return recommendationTypeConfig.requestParamsConfig?.reduce((acc, config) => {
            const value = path((config.dataKey || config.key).split('.'), dataMap[config.dataType]) || config.defaultValue;

            if (value) {
                acc[config.key] = value;
            }

            return acc;
        }, {}) || {};
    }, [dataMap]);
};

export const useFutureAnthemsList = () => {
    const dispatch = useDispatch();
    const filterGameFunction = useGameFilter();
    const { categories } = useGamesSelector();
    const gamesConfiguration = useAppSelector(prop('gamesConfiguration'));
    const { customCategories: { category: customCategories = [] } = {} } = usePortletConfig();
    const {
        isLoggedIn,
        futureAnthemsList
    } = useUserSelector(pick(['isLoggedIn', 'futureAnthemsList']));
    const config = useConfigTag(CONFIG_TAG_TYPE.futureAnthemsConfig)?.config;
    const getFutureAnthemsRequestParams = useFutureAnthemsRequestParams();
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();

    const faRecommendationTypes = useMemo(() => {
        if (!isLoggedIn) {
            return [];
        }

        return categories.reduce((acc, category) => {
            const {
                isOnlyLoggedIn,
                futureAnthemsEnabled,
                futureAnthemsRecommendationType
            } = customCategories.find(({ categoryName }) => categoryName === category.name) || {};

            if (
                futureAnthemsEnabled &&
                !acc.includes(futureAnthemsRecommendationType)
                && (!isOnlyLoggedIn || isOnlyLoggedIn && isLoggedIn)
            ) {
                acc.push({ recommendationType: futureAnthemsRecommendationType });
            }

            return acc;
        }, []);
    }, [categories, customCategories, isLoggedIn]);

    useEffect(() => {
        if (!faRecommendationTypes.length || !config.url) {
            return;
        }

        faRecommendationTypes.forEach(async ({ recommendationType }) => {
            const { isFetchStarted, fetched, updatedAt } = futureAnthemsList[recommendationType] || {};
            const timeDiff = (Date.now() - updatedAt) / (1000 * 60 * 60);

            if (isFetchStarted || fetched && timeDiff < UPDATE_TIME) {
                return;
            }

            const recommendationTypeConfig = config.map.find((FAConfig) => {
                return FAConfig.recommendationType === recommendationType;
            });

            dispatch(setFutureAnthemsGames({
                recommendationType,
                isFetchStarted: true,
                fetched: false
            }));

            const sessionToken = recommendationTypeConfig.requestParamsConfig?.find(({ dataKey }) => dataKey === 'sessionToken') &&
                await getTemporaryAuthenticationToken();

            post({
                url: config.url,
                requestBody: {
                    ...getFutureAnthemsRequestParams(recommendationTypeConfig),
                    recommendationType,
                    ...sessionToken && { sessionToken }
                }
            })
                .then(response => {
                    dispatch(setFutureAnthemsGames({
                        recommendationType,
                        fetched: true,
                        isFetchStarted: false,
                        updatedAt: Date.now(),
                        gamesList: parseResponse({ response, recommendationTypeConfig })
                            .map(({ gameId }) => gameId)
                            .filter((gameCode) => filterGameFunction(gamesConfiguration[gameCode]))
                    }));
                })
                .catch(() => {
                    dispatch(setFutureAnthemsGames({
                        recommendationType,
                        fetched: true,
                        updatedAt: Date.now(),
                        isFetchStarted: false
                    }));
                });
        });
    }, [
        config, dispatch, faRecommendationTypes, filterGameFunction, futureAnthemsList, gamesConfiguration,
        getFutureAnthemsRequestParams, getTemporaryAuthenticationToken
    ]);
};
